import type { ICompareTabGroup, IStackedCards } from "~/types/components/landings.type";
import type { ITitleTextSubContent } from "~/types/global/title-text";
import type { IHomepageHero } from "~/types/pages/homepage";

export const useHomepageSection = () => {
    const { t } = useI18n();
    const auth = useAuth();
    const heroContent: IHomepageHero = {
        title: t("pages_homepage_hero"),
        subtitle: t("pages_homepage_hero_desc"),
        cta: {
            text: t(auth.value ? "global_letsGo" : "global_tryForFree"),
            link: "/",
            id: "hero",
        },
        features: [
            { text: t("pages_homepage_hero_feature_1"), icon: "tabler:currency-dollar" },
            { text: t("pages_homepage_hero_feature_2"), icon: "tabler:bolt" },
        ],
        videosData: [
            {
                id: "homepage-virtualstaging-herosection",
                text: t("pages_homepage_hero_VS"),
                link: "/dashboard/upload-photo/service-ai-virtual-staging",
                image: { src: "/Home-Page/Hero/Small/Virtual-Staging.webp", alt: "VS Hero" },
                mobileImageSrc: "/Home-Page/Hero/Small/VS.png",
                videoSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Home-Page/Hero/Large/VS.mp4",
            },
            {
                id: "homepage-imageenhancement-herosection",
                text: t("pages_homepage_hero_IE"),
                link: "/dashboard/upload-photo/service-image-enhancement",
                image: { src: "/Home-Page/Hero/Small/IE-poster.webp", alt: "PE Hero" },
                mobileImageSrc: "/Home-Page/Hero/Small/IE.png",
                videoSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Home-Page/Hero/Large/IE.mp4",
            },
            {
                id: "homepage-daytodusk-herosection",
                text: t("pages_homepage_hero_DD"),
                link: "/dashboard/upload-photo/service-day-to-dusk",
                image: { src: "/Home-Page/Hero/Small/DD-poster.webp", alt: "PE Hero" },
                mobileImageSrc: "/Home-Page/Hero/Small/DD.png",
                videoSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Home-Page/Hero/Large/DD.mp4",
            },
            {
                id: "homepage-itemremoval-herosection",
                text: t("pages_homepage_hero_IR"),
                link: "/dashboard/upload-photo/service-item-removal",
                image: { src: "/Home-Page/Hero/Small/IR-poster.webp", alt: "ID Hero" },
                mobileImageSrc: "/Home-Page/Hero/Small/IR.png",
                videoSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Home-Page/Hero/Large/IR.mp4",
            },
        ],
    };

    const achievementItems: ITitleTextSubContent[] = [
        {
            text: t("pages_homepage_hero_achievement_1_title"),
            subtitle: t("pages_homepage_hero_achievement_1_subtitle"),
        },
        {
            text: t("pages_homepage_hero_achievement_2_title"),
            subtitle: t("pages_homepage_hero_achievement_2_subtitle"),
        },
        {
            text: t("pages_homepage_hero_achievement_3_title"),
            subtitle: t("pages_homepage_hero_achievement_3_subtitle"),
        },
    ];

    const retailers = {
        title: t("pages_homepage_retailers_title"),
        items: [
            {
                src: "/logo/logo1.webp",
                alt: "keller williams logo",
            },
            {
                src: "/logo/logo2.webp",
                alt: "remax logo",
            },
            {
                src: "/logo/logo3.webp",
                alt: "voldwell banker logo",
            },
            {
                src: "/logo/logo4.webp",
                alt: "century 21 logo",
            },
            {
                src: "/logo/logo5.webp",
                alt: "compass logo",
            },
        ],
    };

    const faqFirstRow = [
        {
            title: t("pages_homepage_faq_q1"),
            content: t("pages_homepage_faq_a1"),
        },
        {
            title: t("pages_homepage_faq_q2"),
            content: t("pages_homepage_faq_a2"),
        },
        {
            title: t("pages_homepage_faq_q3"),
            content: t("pages_homepage_faq_a3_test"),
        },
        {
            title: t("pages_homepage_faq_q4"),
            content: t("pages_homepage_faq_a4"),
        },
    ];

    const faqSecondRow = [
        {
            title: t("pages_homepage_faq_q5"),
            content: t("pages_homepage_faq_a5"),
        },
        {
            title: t("pages_homepage_faq_q6"),
            content: t("pages_homepage_faq_a6"),
        },
        {
            title: t("pages_homepage_faq_q7"),
            content: t("pages_homepage_faq_a7"),
        },
        {
            title: t("pages_homepage_faq_q8"),
            content: t("pages_homepage_faq_a8") + " support@aihomedesign.com",
        },
    ];

    const aboutID = {
        title: t("pages_homepage_about_title"),
        description: useDevice().isDesktop
            ? [t("pages_homepage_about_desc_1"), t("pages_homepage_about_desc_2")]
            : [t("pages_homepage_about_desc_1")],
        moreDescription: useDevice().isDesktop ? [] : [t("pages_homepage_about_desc_2")],
    };

    const realEstateHubTools: ICompareTabGroup = {
        title: t("pages_homepage_servicesTabGroups_realEstateHubTools"),
        items: [
            {
                title: t("global_all"),
                id: "homepage-realestatehubsection-all-button",
                content: [
                    {
                        title: t("global_serviceName_virtualStaging"),
                        id: "homepage-realestatehubsection-aivscard",
                        value: "service-ai-virtual-staging",
                        description: t("global_serviceName_virtualStaging_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Virtual-Staging/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Virtual-Staging/Large/2-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-ai-virtual-staging",
                        },
                    },
                    {
                        title: t("global_serviceName_humanVirtualStaging"),
                        id: "homepage-realestatehubsection-vsbyhumancard",
                        value: "service-vs-manual",
                        description: t("global_serviceName_humanVirtualStaging_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Virtual-Staging-By-Human/Large/2-After.webp" },
                            before: { src: "/Home-Page/Virtual-Staging-By-Human/Large/2-Before.webp" },
                        },
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-vs-manual",
                        },
                    },
                    {
                        id: "homepage-realestatehubsection-imageenhancementcard",
                        title: t("global_serviceName_imageEnhancement"),
                        value: "service-image-enhancement",
                        description: t("global_serviceName_imageEnhancement_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Image-Enhancement/Large/05-After.webp" },
                            before: { src: "/Home-Page/Image-Enhancement/Large/05-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-image-enhancement",
                        },
                    },
                    {
                        title: t("global_serviceName_itemRemoval"),
                        value: "service-item-removal",
                        id: "homepage-realestatehubsection-itemremovalcard",
                        description: t("global_serviceName_itemRemoval_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Item-Removal/Large/01-After.webp" },
                            before: { src: "/Home-Page/Item-Removal/Large/01-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-item-removal",
                        },
                    },
                    {
                        title: t("global_serviceName_dayToDusk"),
                        value: "service-day-to-dusk",
                        id: "homepage-realestatehubsection-daytoduskcard",
                        description: t("global_serviceName_dayToDusk_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Day-to-Dusk/Large/2-After.webp" },
                            before: { src: "/Home-Page/Day-to-Dusk/Large/2-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-day-to-dusk",
                        },
                    },
                ],
            },
            {
                title: t("pages_homepage_hero_VS"),
                id: "homepage-realestatehubsection-virtualstaging-button",
                content: [
                    {
                        id: "homepage-realestatehubsection-aivscard",
                        title: t("global_serviceName_virtualStaging"),
                        value: "service-ai-virtual-staging",
                        description: t("global_serviceName_virtualStaging_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Virtual-Staging/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Virtual-Staging/Large/2-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-ai-virtual-staging",
                        },
                    },
                    // {
                    //     title: t("global_serviceName_virtualReStaging"),
                    //     id: "homepage-realestatehubsection-airestagingcard",
                    //     value: "service-ai-virtual-restaging",
                    //     description: t("global_serviceName_virtualReStaging_desc"),
                    //     compareImages: {
                    //         after: { src: "/Home-Page/AI-Virtual-Restaging/Large/2-After.webp" },
                    //         before: { src: "/Home-Page/AI-Virtual-Restaging/Large/2-Before.webp" },
                    //     },
                    //     cta: {
                    //         text: t("global_use"),
                    //         link: "/dashboard/upload-photo/service-ai-virtual-restaging",
                    //     },
                    //     disabled: true,
                    //     type: "real-estate-hub",
                    // },
                    {
                        title: t("global_serviceName_humanVirtualStaging"),
                        id: "homepage-realestatehubsection-vsbyhumancard",
                        value: "service-vs-manual",
                        description: t("global_serviceName_humanVirtualStaging_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Virtual-Staging-By-Human/Large/2-After.webp" },
                            before: { src: "/Home-Page/Virtual-Staging-By-Human/Large/2-Before.webp" },
                        },
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-vs-manual",
                        },
                    },
                ],
            },
            {
                id: "homepage-realestatehubsection-photoediting-button",
                title: t("pages_homepage_hero_PE"),
                content: [
                    {
                        id: "homepage-realestatehubsection-imageenhancementcard",
                        title: t("global_serviceName_imageEnhancement"),
                        value: "service-image-enhancement",
                        description: t("global_serviceName_imageEnhancement_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Image-Enhancement/Large/05-After.webp" },
                            before: { src: "/Home-Page/Image-Enhancement/Large/05-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-image-enhancement",
                        },
                    },
                    {
                        title: t("global_serviceName_itemRemoval"),
                        id: "homepage-realestatehubsection-itemremovalcard",
                        value: "service-item-removal",
                        description: t("global_serviceName_itemRemoval_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Item-Removal/Large/01-After.webp" },
                            before: { src: "/Home-Page/Item-Removal/Large/01-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-item-removal",
                        },
                    },
                    {
                        title: t("global_serviceName_dayToDusk"),
                        id: "homepage-realestatehubsection-daytoduskcard",
                        value: "service-day-to-dusk",
                        description: t("global_serviceName_dayToDusk_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Day-to-Dusk/Large/2-After.webp" },
                            before: { src: "/Home-Page/Day-to-Dusk/Large/2-Before.webp" },
                        },
                        type: "real-estate-hub",
                        cta: {
                            text: t("global_use"),
                            link: "/dashboard/upload-photo/service-day-to-dusk",
                        },
                    },
                ],
            },
        ],
    };

    const designHubTools: ICompareTabGroup = {
        title: t("pages_homepage_servicesTabGroups_designHubTools"),
        items: [
            {
                title: t("global_all"),
                id: "homepage-designhubsection-all-button",
                content: [
                    {
                        title: t("global_serviceName_creativeDesigner"),
                        id: "homepage-designhubsection-creativedesignercard",
                        description: t("global_serviceName_creativeDesigner_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Creative-Design/Large/2-After.webp" },
                            before: { src: "/Home-Page/Creative-Design/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-creative-designer" },
                    },
                    {
                        title: t("global_serviceName_fullSetReplacement"),
                        id: "homepage-designhubsection-fullsetreplacemnetcard",
                        value: "service-replace-furniture-set",
                        description: t("global_serviceName_fullSetReplacement_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Full-Set-Replacement/Large/2-After.webp" },
                            before: { src: "/Home-Page/Full-Set-Replacement/Large/2-Before.webp" },
                        },
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-replace-furniture-set" },
                        type: "design-hub",
                    },
                    {
                        title: t("global_serviceName_furnitureReplacement"),
                        id: "homepage-designhubsection-furniturereplacementcard",
                        value: "service-replace-furniture",
                        description: t("global_serviceName_furnitureReplacement_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Furniture-Replacement/Large/2-After.webp" },
                            before: { src: "/Home-Page/Furniture-Replacement/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-replace-furniture" },
                    },
                    {
                        title: t("global_serviceName_wallChange"),
                        id: "homepage-designhubsection-wallchangecard",
                        value: "service-change-your-wall",
                        description: t("global_serviceName_wallChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Wall-Change/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Wall-Change/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        disabled: false,
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-change-your-wall" },
                    },
                    {
                        title: t("global_serviceName_floorChange"),
                        id: "homepage-designhubsection-floorchangecard",
                        value: "service-change-the-floor",
                        description: t("global_serviceName_floorChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Floor-Change/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Floor-Change/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-change-the-floor" },
                    },
                    {
                        title: t("global_serviceName_ceilingChange"),
                        id: "homepage-designhubsection-ceilingchangecard",
                        value: "service-ceiling-change",
                        description: t("global_serviceName_ceilingChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Ceiling-Change/Large/3-After.webp" },
                            before: { src: "/Home-Page/AI-Ceiling-Change/Large/3-Before.webp" },
                        },
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-ceiling-change" },
                        // disabled: true,
                        type: "design-hub",
                    },
                    {
                        title: t("global_serviceName_backSplashChange"),
                        id: "homepage-designhubsection-backsplashcard",
                        value: "service-backsplash",
                        description: t("global_serviceName_backSplashChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Backsplash-Change/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Backsplash-Change/Large/2-Before.webp" },
                        },
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-backsplash" },
                        type: "design-hub",
                    },
                    {
                        title: t("global_serviceName_underConstruction"),
                        id: "homepage-designhubsection-under-construction",
                        value: "service-under-construction",
                        description: t("global_serviceName_underConstruction_desc"),
                        compareImages: {
                            after: {
                                src: "/Under-Construction/1-A.webp",
                            },
                            before: { src: "/Under-Construction/1.webp" },
                        },
                        type: "design-hub",
                        disabled: false,
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-backsplash" },
                    },
                ],
            },
            {
                title: t("pages_homepage_hero_ID"),
                id: "homepage-designhubsection-interiordesign-button",
                content: [
                    {
                        title: t("global_serviceName_creativeDesigner"),
                        id: "homepage-designhubsection-creativedesignercard",
                        description: t("global_serviceName_creativeDesigner_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Creative-Design/Large/2-After.webp" },
                            before: { src: "/Home-Page/Creative-Design/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-creative-designer" },
                    },
                    {
                        title: t("global_serviceName_fullSetReplacement"),
                        id: "homepage-designhubsection-fullsetreplacemnetcard",
                        value: "service-replace-furniture-set",
                        description: t("global_serviceName_fullSetReplacement_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Full-Set-Replacement/Large/2-After.webp" },
                            before: { src: "/Home-Page/Full-Set-Replacement/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-replace-furniture-set" },
                    },
                    {
                        title: t("global_serviceName_furnitureReplacement"),
                        id: "homepage-designhubsection-furniturereplacemnetcard",
                        value: "service-replace-furniture",
                        description: t("global_serviceName_furnitureReplacement_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/Furniture-Replacement/Large/2-After.webp" },
                            before: { src: "/Home-Page/Furniture-Replacement/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-replace-furniture" },
                    },
                ],
            },
            {
                title: t("components_header_HR_title"),
                id: "homepage-designhubsection-homerenovation-button",
                content: [
                    {
                        title: t("global_serviceName_wallChange"),
                        id: "homepage-designhubsection-wallchangecard",
                        value: "service-change-your-wall",
                        description: t("global_serviceName_wallChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Wall-Change/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Wall-Change/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        disabled: false,
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-change-your-wall" },
                    },
                    {
                        title: t("global_serviceName_floorChange"),
                        id: "homepage-designhubsection-floorchangecard",
                        value: "service-change-the-floor",
                        description: t("global_serviceName_floorChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Floor-Change/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Floor-Change/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-change-the-floor" },
                    },
                    {
                        title: t("global_serviceName_ceilingChange"),
                        id: "homepage-designhubsection-ceilingchangecard",
                        value: "service-ceiling-change",
                        description: t("global_serviceName_ceilingChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Ceiling-Change/Large/3-After.webp" },
                            before: { src: "/Home-Page/AI-Ceiling-Change/Large/3-Before.webp" },
                        },
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-ceiling-change" },
                        // disabled: true,
                        type: "design-hub",
                    },
                    {
                        title: t("global_serviceName_backSplashChange"),
                        id: "homepage-designhubsection-backsplashcard",
                        value: "service-backsplash",
                        description: t("global_serviceName_backSplashChange_desc"),
                        compareImages: {
                            after: { src: "/Home-Page/AI-Backsplash-Change/Large/2-After.webp" },
                            before: { src: "/Home-Page/AI-Backsplash-Change/Large/2-Before.webp" },
                        },
                        type: "design-hub",
                    },
                    {
                        title: t("global_serviceName_underConstruction"),
                        id: "homepage-designhubsection-under-construction",
                        value: "service-under-construction",
                        description: t("global_serviceName_underConstruction_desc"),
                        compareImages: {
                            after: {
                                src: "/Under-Construction/1-A.webp",
                            },
                            before: { src: "/Under-Construction/1.webp" },
                        },
                        type: "design-hub",
                        disabled: true,
                        cta: { text: t("global_use"), link: "/dashboard/upload-photo/service-backsplash" },
                    },
                ],
            },
        ],
    };

    const stackedCards: IStackedCards = {
        title: t("pages_homepage_glance_title"),
        items: [
            {
                title: t("pages_homepage_glance_VS_title"),
                smallTitle: t("pages_homepage_glance_VS_subtitle"),
                image: { src: "Landings/Home-Page/LandingHomeCard01-VS.webp", alt: "virtual staging before and after" },

                secondaryCta: {
                    text: t("global_learnMore"),
                    id: "homepage-vssection-learnmore",
                    link: "/virtual-staging",
                },
                primaryCta: {
                    text: t("global_tryForFree"),
                    id: "homepage-vssection-tryforfree",
                    link: "/dashboard/upload-photo/service-ai-virtual-staging",
                },
                items: [
                    {
                        icon: "tabler:edit",
                        text: t("pages_homepage_glance_VS_item_1"),
                    },
                    {
                        icon: "tabler:color-swatch",
                        text: t("pages_homepage_glance_VS_item_2"),
                    },
                    {
                        icon: "tabler:eraser",
                        text: t("pages_homepage_glance_VS_item_3"),
                    },
                ],
            },
            {
                title: t("pages_homepage_glance_IR_title"),
                smallTitle: t("pages_homepage_glance_IR_subtitle"),
                image: { src: "Landings/Home-Page/LandingHomeCard02-IR.webp", alt: "item removal before and after" },

                secondaryCta: {
                    text: t("global_learnMore"),
                    id: "homepage-irsection-learnmore",
                    link: "/item-removal",
                },
                primaryCta: {
                    text: t("global_tryForFree"),
                    id: "homepage-irsection-tryforfree",
                    link: "/dashboard/upload-photo/service-item-removal",
                },
                items: [
                    {
                        icon: "tabler:eraser",
                        text: t("pages_homepage_glance_IR_item_1"),
                    },
                    {
                        icon: "tabler:brush",
                        text: t("pages_homepage_glance_IR_item_2"),
                    },
                ],
            },
            {
                title: t("pages_homepage_glance_IE_title"),
                smallTitle: t("pages_homepage_glance_IE_subtitle"),
                image: {
                    src: "Landings/Home-Page/LandingHomeCard03-PE.webp",
                    alt: "image enhancement before and after",
                },

                secondaryCta: {
                    text: t("global_learnMore"),
                    id: "homepage-iesection-learnmore",
                    link: "/image-enhancement",
                },
                primaryCta: {
                    text: t("global_tryForFree"),
                    id: "homepage-iesection-tryforfree",
                    link: "/dashboard/upload-photo/service-image-enhancement",
                },
                items: [
                    {
                        icon: "tabler:device-tv",
                        text: t("pages_homepage_glance_IE_item_1"),
                    },
                    {
                        icon: "tabler:cloud",
                        text: t("pages_homepage_glance_IE_item_2"),
                    },
                    {
                        icon: "tabler:tools",
                        text: t("pages_homepage_glance_IE_item_3"),
                    },
                ],
            },
            {
                title: t("pages_homepage_glance_DD_title"),
                smallTitle: t("pages_homepage_glance_DD_subtitle"),
                image: { src: "Landings/Home-Page/LandingHomeCard04-DD.webp", alt: "day to dusk before and after" },

                secondaryCta: {
                    text: t("global_learnMore"),
                    id: "homepage-ddsection-learnmore",
                    link: "/day-to-dusk",
                },
                primaryCta: {
                    text: t("global_tryForFree"),
                    id: "homepage-ddsection-tryforfree",
                    link: "/dashboard/upload-photo/service-day-to-dusk",
                },
                items: [
                    {
                        icon: "tabler:shadow",
                        text: t("pages_homepage_glance_DD_item_1"),
                    },
                    {
                        icon: "tabler:cloud",
                        text: t("pages_homepage_glance_DD_item_2"),
                    },
                    {
                        icon: "tabler:sunset-2",
                        text: t("pages_homepage_glance_DD_item_3"),
                    },
                ],
            },
        ],
    };

    return {
        aboutID,
        retailers,
        faqFirstRow,
        heroContent,
        faqSecondRow,
        stackedCards,
        designHubTools,
        achievementItems,
        realEstateHubTools,
    };
};
